"use strict";
/**
 * @fileoverview Shader uniforms module.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CubeCameraUniform = exports.CubeCameraUniformImpl = exports.isCubeTextureUniform = exports.CubeTextureUniform = exports.CubeTextureImpl = exports.Texture2DUniform = exports.Texture2DUniformImpl = exports.isTextureUniform = exports.TextureUniform = exports.PerspectiveMatUniform = exports.ViewMatUniform = exports.ViewMatUniformImpl = exports.NormalMatUniform = exports.ModelMatUniform = exports.Mat4Uniform = exports.Mat3Uniform = exports.Mat2Uniform = exports.Vec4Uniform = exports.Vec3Uniform = exports.Vec2Uniform = exports.IntegerUniform = exports.FloatUniform = exports.BooleanUniform = exports.Uniform = void 0;
const gl_1 = require("./gl");
const math_1 = require("./math");
/**
 * Uniform base class. All uniforms are a superclass of this
 * class.
 */
class Uniform {
    constructor(type_, name, dataOrCb_) {
        this.type_ = type_;
        this.name = name;
        this.dataOrCb_ = dataOrCb_;
    }
    /**
     * Check that the type of a uniform matches the expected type.
     */
    static checkType(u, wantType) {
        if (u.type_ !== wantType) {
            throw new TypeError(`Expected uniform with type ${wantType} got type ${u.type_}`);
        }
    }
    /**
     * Get the data or callback to compute the data for the uniform.
     * Static because this method should only be used in phixl's internals.
     */
    static dataOrCallback(u) {
        return u.dataOrCb_;
    }
    /**
     * Set the data the uniform object will send to the shader.
     * @param dataOrCb new data or a callback to compute the new data.
     */
    set(dataOrCb) {
        this.dataOrCb_ = dataOrCb;
        return this;
    }
    /**
     * Get the data the uniform will send to the shader.
     */
    data() {
        if (typeof this.dataOrCb_ === 'function') {
            return this.dataOrCb_();
        }
        return this.dataOrCb_;
    }
    /**
     * Send the uniform data to the shader.
     */
    send(gl, program) {
        throw new Error('Virtual method should not be invoked');
    }
}
exports.Uniform = Uniform;
/**
 *
 */
class BytesUniform extends Uniform {
    /**
     * Send the uniform data to the shader.
     */
    send(gl, program) {
        if (isNaN(this.data())) {
            throw TypeError(`Data for ${this.type_} uniform should be a number`);
        }
        gl_1.sendBytesUniform(gl, program, this.name, this.type_, this.data());
    }
}
/**
 * Create a builder function for each type of numeric uniform.
 */
const bytesUniform = (type) => (name, data) => new BytesUniform(type, name, data);
/**
 * Send a boolean uniform to a shader.
 * @param name of the uniform in the shader
 * @param data the uniform will send to the shader
 */
exports.BooleanUniform = bytesUniform(gl_1.UniformType.BOOLEAN);
/**
 * Send a float uniform to a shader.
 * @param name of the uniform in the shader
 * @param data the uniform will send to the shader
 */
exports.FloatUniform = bytesUniform(gl_1.UniformType.FLOAT);
/**
 * Send a integer uniform to a shader.
 * @param name of the uniform in the shader
 * @param data the uniform will send to the shader
 */
exports.IntegerUniform = bytesUniform(gl_1.UniformType.INTEGER);
/**
 * Abstraction for sending sequence uniforms to a shader.
 */
class SequenceUniform extends Uniform {
    constructor(type, name, dimension, dataOrCb) {
        super(type, name, dataOrCb);
        this.dimension = dimension;
    }
    /**
     * Validate the sequence uniform's data.
     */
    validateData_() {
        if (this.type_ == gl_1.UniformType.VECTOR) {
            if (this.data().length != this.dimension) {
                throw new TypeError(`Dimension mismatch for a ${this.type_}${this.dimension} uniform`);
            }
        }
        else {
            if (this.data().length != Math.pow(this.dimension, 2)) {
                throw new TypeError(`Dimension mismatch for a ${this.type_}${this.dimension} uniform`);
            }
        }
    }
    /**
     * Send the data to a shader.
     */
    send(gl, program) {
        this.validateData_();
        if (this.type_ === gl_1.UniformType.VECTOR) {
            gl_1.sendVectorUniform(gl, program, this.name, this.dimension, this.data());
        }
        else {
            gl_1.sendMatrixUniform(gl, program, this.name, this.dimension, this.data());
        }
    }
    /**
     * Set the uniform's data.
     * @param dataOrCb new data or a callback which computes the data.
     */
    set(dataOrCb) {
        this.dataOrCb_ = dataOrCb;
        this.validateData_();
        return this;
    }
    /**
     * Check the dimension of a uniform.
     */
    static checkDimension(u, wantDimension) {
        if (u.dimension != wantDimension) {
            throw new TypeError();
        }
    }
}
/**
 * Creates builder functions for vector and matrix uniforms.
 */
const sequenceUniform = (type, dimension) => (name, data) => new SequenceUniform(type, name, dimension, data);
/**
 * Sends a 2-dimensional vector to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Vec2Uniform = sequenceUniform(gl_1.UniformType.VECTOR, 2);
/**
 * Sends a 3-dimensional vector to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Vec3Uniform = sequenceUniform(gl_1.UniformType.VECTOR, 3);
/**
 * Sends a 4-dimensional vector to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Vec4Uniform = sequenceUniform(gl_1.UniformType.VECTOR, 4);
/**
 * Sends a 2-dimensional matrix to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Mat2Uniform = sequenceUniform(gl_1.UniformType.MATRIX, 2);
/**
 * Sends a 3-dimensional matrix to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Mat3Uniform = sequenceUniform(gl_1.UniformType.MATRIX, 3);
/**
 * Sends a 4-dimensional matrix to a shader.
 * @param name of the uniform
 * @param data for the uniform
 */
exports.Mat4Uniform = sequenceUniform(gl_1.UniformType.MATRIX, 4);
/**
 * Abstraction for sending a model matrix to a shader
 * as a uniform.
 */
class ModelMatUniformImpl extends SequenceUniform {
    constructor(name, opts) {
        super(gl_1.UniformType.MATRIX, name, 4, () => this.matrix_());
        this.scaleMatrix_ = math_1.identity(4);
        if (opts.scale) {
            if (typeof opts.scale === 'number') {
                this.scaleMatrix_ = math_1.scale(this.scaleMatrix_, opts.scale);
            }
            else {
                this.scaleMatrix_ = math_1.scale(this.scaleMatrix_, ...opts.scale);
            }
        }
        this.rotationMatrix_ = math_1.identity(3);
        if (opts.rotate) {
            this.rotationMatrix_ = math_1.rotate(this.rotationMatrix_, ...opts.rotate);
        }
        this.translation_ = [0, 0, 0];
        if (opts.translate)
            this.translation_ = opts.translate;
    }
    /**
     * Get the scale component for the model matrix as a 4D matrix.
     */
    scaleMatrix() {
        return [...this.scaleMatrix_];
    }
    /**
     * Get the rotation component of the model matrix as a 3D matrix.
     */
    rotationMatrix() {
        return [...this.rotationMatrix_];
    }
    /**
     * Gets the rotation matrix as a 4D matrix.
     */
    rotationMatrix4_() {
        const R = this.rotationMatrix_;
        return [
            R[0], R[1], R[2], 0,
            R[3], R[4], R[5], 0,
            R[6], R[7], R[8], 0,
            0, 0, 0, 1,
        ];
    }
    /**
     * Gets the translation component of the model matrix as a 3D vector.
     */
    translation() {
        return [...this.translation_];
    }
    /**
     * Get the model matrix this uniform will send as a 4D matrix.
     */
    matrix_() {
        return math_1.translate(math_1.multiply(this.rotationMatrix4_(), this.scaleMatrix()), ...this.translation_);
    }
    /**
     * Apply a new scale to the model matrix.
     */
    scale(...args) {
        this.scaleMatrix_ = math_1.scale(this.scaleMatrix_, ...args);
    }
    /**
     * Set the scale of the model matrix.
     */
    setScale(...args) {
        this.scaleMatrix_ = math_1.scale(math_1.identity(4), ...args);
    }
    /**
     * Apply a rotation to the model matrix.
     * @param theta angle of rotation in radians
     * @param axis of rotation
     */
    rotate(theta, ...axis) {
        this.rotationMatrix_ = math_1.rotate(this.rotationMatrix_, theta, ...axis);
    }
    /**
     * Set the rotation component of the model matrix.
     * @param theta angle of rotation in radians
     * @param axis of rotation
     */
    setRotate(theta, ...axis) {
        this.rotationMatrix_ = math_1.rotate(math_1.identity(3), theta, ...axis);
    }
    /**
     * Apply a translation to the model matrix.
     */
    translate(...args) {
        args = args.slice(0, 3);
        if (!math_1.isVector3(args)) {
            throw new Error('Expected 3 numeric arguments');
        }
        this.translation_ = math_1.add(this.translation_, args);
    }
    /**
     * Set the translation component of the model matrix.
     */
    setTranslation(...args) {
        args = args.slice(0, 3);
        if (!math_1.isVector3(args)) {
            throw new Error('Expected 3 numeric arguments');
        }
        this.translation_ = args;
    }
}
/**
 * Sends a model matrix to a shader as a uniform that applies
 * a scale, rotation, and translation (in that order).
 */
exports.ModelMatUniform = (name, opts = {}) => new ModelMatUniformImpl(name, opts);
/**
 * Sends the resulting normal matrix for a given
 * model matrix to a shader.
 *
 * The normal matrix is the inverse transpose of
 * the rotation component of the model matrix.
 * Since for any rotation matrix R, it's inverse is
 * R^T.
 */
exports.NormalMatUniform = (name, modelMat) => {
    return sequenceUniform(gl_1.UniformType.MATRIX, 3)(name, () => modelMat.rotationMatrix());
};
/**
 * View matrix uniform with accessor and mutator methods for
 * the
 */
class ViewMatUniformImpl extends SequenceUniform {
    constructor(name, eye_, at_, up_) {
        super(gl_1.UniformType.MATRIX, name, 4, () => math_1.lookAt(this.eye_, this.at_, this.up_));
        this.eye_ = eye_;
        this.at_ = at_;
        this.up_ = up_;
    }
    /**
     * Get the eye vector for the view matrix.
     */
    eye() {
        return [...this.eye_];
    }
    /**
     * Get the look at vector for the view matrix.
     */
    at() {
        return [...this.at_];
    }
    /**
     * Get the up vector for the view matrix.
     */
    up() {
        return [...this.up_];
    }
    /**
     * Set the eye vector.
     */
    setEye(...eye) {
        ViewMatUniformImpl.validateVector3(eye);
        this.eye_ = eye;
    }
    /**
     * Set the look at vector.
     */
    setAt(...at) {
        ViewMatUniformImpl.validateVector3(at);
        this.at_ = at.slice(0, 3);
    }
    /**
     * Set the up vector.
     */
    setUp(...up) {
        ViewMatUniformImpl.validateVector3(up);
        this.up_ = [...up];
    }
}
exports.ViewMatUniformImpl = ViewMatUniformImpl;
/**
 * Checks if the provided data is a 3D vector.
 * @param data to validate
 */
ViewMatUniformImpl.validateVector3 = (data) => {
    if (!math_1.isVector3(data)) {
        throw new Error('Expected 3 numbers as arguments');
    }
};
/**
 * Sends a view matrix uniform for the given
 * eye, at, and up vectors.
 */
exports.ViewMatUniform = (name, eye, at, up) => new ViewMatUniformImpl(name, eye, at, up);
/**
 * Abstraction for a perspective matrix uniform.
 */
class PerspectiveMatUniformImpl extends SequenceUniform {
    constructor(name, fovy_, aspect_, near_, far_) {
        super(gl_1.UniformType.MATRIX, name, 4, () => math_1.perspective(this.fovy_, this.aspect_, this.near_, this.far_));
        this.fovy_ = fovy_;
        this.aspect_ = aspect_;
        this.near_ = near_;
        this.far_ = far_;
    }
    /**
     * Get the field of view in radians.
     */
    fovy() {
        return this.fovy_;
    }
    /**
     * Get the aspect ratio (width / height).
     */
    aspect() {
        return this.aspect_;
    }
    /**
     * Get the near plane.
     */
    near() {
        return this.near_;
    }
    /**
     * Get the far plane.
     */
    far() {
        return this.far_;
    }
    /**
     * Set the field of view in radians.
     */
    setFovy(fovy) {
        this.fovy_ = fovy;
    }
    /**
     * Set the aspect ratio (width / height).
     */
    setAspect(aspect) {
        this.aspect_ = aspect;
    }
    /**
     * Set the near plane.
     */
    setNear(near) {
        this.near_ = near;
    }
    /**
     * Set the far plane.
     */
    setFar(far) {
        this.far_ = far;
    }
}
/**
 * Sends a perspective 4D matrix uniform.
 */
exports.PerspectiveMatUniform = (name, fovy, aspect, near, far = null) => new PerspectiveMatUniformImpl(name, fovy, aspect, near, far);
/**
 * Abstraction for a texture uniform.
 */
class TextureUniform extends Uniform {
    /**
     * Check if the uniform needs to build the texture.
     */
    shouldBuildTextureFromData_() {
        return (!this.textureBuffers_) // If this object exists then 
            && (!!this.dataOrCb_)
            && (!this.texture_ || gl_1.isVideo(this.data()));
    }
    /**
     * Prepare the texture before using it in a shader.
     */
    prepare(gl, p) {
        throw new Error('Virtual method should not be invoked');
    }
    /**
     * Get the WebGLFramebuffer and WebGLRenderbuffer to render a shader
     * to this texture.
     */
    buffers(gl, v) {
        throw new Error('Virtual method should not be invoked');
    }
    /**
     * Set new data for the uniform.
     * @param dataOrCb the new data or a callback computing the data.
     */
    set(dataOrCb) {
        this.dataOrCb_ = dataOrCb;
        this.texture_ = undefined;
        return this;
    }
}
exports.TextureUniform = TextureUniform;
/**
 * Tests if a uniform is for a texture.
 */
exports.isTextureUniform = (u) => u instanceof TextureUniform;
/**
 * Implementation of a 2D texture uniform.
 */
class Texture2DUniformImpl extends TextureUniform {
    constructor(name, dataOrCb) {
        super(gl_1.UniformType.TEXTURE, name, dataOrCb);
    }
    /**
     * Prepare the 2D texture before using it in a shader.
     */
    prepare(gl, program) {
        if (isNaN(this.offset_)) {
            this.offset_ = gl_1.newTextureOffset(program, this.name);
        }
        if (this.shouldBuildTextureFromData_()) {
            this.texture_ = gl_1.texture2d(gl, this.data());
        }
    }
    /**
     * Send the 2D texture to a shader.
     */
    send(gl, program) {
        gl_1.send2DTexture(gl, program, this.name, this.offset_, this.texture_);
    }
    /**
     * Get the buffers needed to render a shader to this texture.
     */
    buffers(gl, viewport) {
        if (this.textureBuffers_)
            return this.textureBuffers_;
        const frameBuffer = gl.createFramebuffer();
        const [, , width, height] = viewport;
        this.textureBuffers_ = {
            frameBuffer,
            renderBuffer: gl_1.renderBuffer(gl, frameBuffer, width, height),
        };
        this.texture_ = gl_1.texture2DFromFramebuffer(gl, frameBuffer, width, height);
        return this.textureBuffers_;
    }
}
exports.Texture2DUniformImpl = Texture2DUniformImpl;
/**
 * Sends a 2D texture uniform to a shader.
 */
exports.Texture2DUniform = (name, data) => new Texture2DUniformImpl(name, data);
/**
 * Implementation of a cube texture.
 */
class CubeTextureImpl extends TextureUniform {
    /**
     * Validate that the data is a cube.
     */
    validateData() {
        if (!math_1.isCube(this.data())) {
            throw new Error('You must provide an object with "posx", "negx", "posy", "negy", ' +
                '"posz", and "negz" keys');
        }
    }
    /**
     * Set the data of the cube texture.
     * @param dataOrCb new data or callback that computes the data.
     */
    set(dataOrCb) {
        this.dataOrCb_ = dataOrCb;
        this.validateData();
        this.texture_ = undefined;
        return this;
    }
    /**
     * Prepare the cube texture to be used in a shader.
     */
    prepare(gl, program) {
        if (isNaN(this.offset_)) {
            this.offset_ = gl_1.newTextureOffset(program, this.name);
        }
        if (this.shouldBuildTextureFromData_()) {
            this.texture_ = gl_1.cubeTexure(gl, this.data());
        }
    }
    /**
     * Send the cube texture to a shader.
     */
    send(gl, program) {
        gl_1.sendCubeTexture(gl, program, this.name, this.offset_, this.texture_);
    }
    /**
     * Get a Cube of buffers needed to render a shader to the cube texture.
     */
    buffers(gl, viewport) {
        if (this.textureBuffers_)
            return this.textureBuffers_;
        const [, , width, height] = viewport;
        const frameBufferCube = math_1.cube(() => gl.createFramebuffer());
        this.textureBuffers_ = {
            frameBuffer: frameBufferCube,
            renderBuffer: math_1.cube((cf) => gl_1.renderBuffer(gl, frameBufferCube[cf], width, height)),
        };
        this.texture_ = gl_1.cubeTextureFromFramebuffer(gl, this.buffers(gl, viewport).frameBuffer, width, height);
        return this.textureBuffers_;
    }
}
exports.CubeTextureImpl = CubeTextureImpl;
/**
 * Sends a cube texture uniform to a shader.
 */
exports.CubeTextureUniform = (name, data) => new CubeTextureImpl(gl_1.UniformType.TEXTURE, name, data);
/**
 * Tests if a uniform is a cube texture uniform.
 */
exports.isCubeTextureUniform = (u) => u instanceof CubeTextureImpl;
/**
 * Implementation of a cube camera uniform.
 */
class CubeCameraUniformImpl extends CubeTextureImpl {
    constructor(name, position_, viewMat_, perspectiveMat_) {
        super(gl_1.UniformType.TEXTURE, name);
        this.position_ = position_;
        this.viewMat_ = viewMat_;
        this.perspectiveMat_ = perspectiveMat_;
        if (!math_1.isVector3(position_)) {
            throw new Error('Expected array of 3 arguments as the first argument');
        }
        Uniform.checkType(viewMat_, gl_1.UniformType.MATRIX);
        SequenceUniform.checkDimension(viewMat_, 4);
    }
    /**
     * You should not use this method with a cube camera uniform.
     */
    set(dataOrCb) {
        throw new Error('Cube camera uniforms should get their data from a shader');
    }
    /**
     * Render a scene to a cube camera texture. Should only be used by
     * phixl internals.
     */
    render(renderShader) {
        const fovy = this.perspectiveMat_.fovy();
        this.perspectiveMat_.setFovy(Math.PI / 2);
        const viewMatDataOrCb = Uniform.dataOrCallback(this.viewMat_);
        for (const cf of math_1.cubeFaces()) {
            const at = math_1.add(CubeCameraUniformImpl.atVectors[cf], this.position_);
            const up = CubeCameraUniformImpl.upVectors[cf];
            this.viewMat_.set(math_1.lookAt(this.position_, at, up));
            renderShader(cf);
        }
        this.viewMat_.set(viewMatDataOrCb);
        this.perspectiveMat_.setFovy(fovy);
    }
}
exports.CubeCameraUniformImpl = CubeCameraUniformImpl;
CubeCameraUniformImpl.upVectors = {
    posx: [0, -1, 0],
    negx: [0, -1, 0],
    posy: [0, 0, 1],
    negy: [0, 0, 1],
    posz: [0, -1, 0],
    negz: [0, -1, 0],
};
CubeCameraUniformImpl.atVectors = {
    posx: [1, 0, 0],
    negx: [-1, 0, 0],
    posy: [0, 1, 0],
    negy: [0, -1, 0],
    posz: [0, 0, 1],
    negz: [0, 0, -1],
};
/**
 * Sends a cube texture to a shader rendered from
 * a cube camera.
 */
exports.CubeCameraUniform = (name, position, viewMat, perspectiveMat) => {
    if (!position)
        position = [0, 0, 0];
    if (!viewMat) {
        viewMat = new ViewMatUniformImpl('', [0, 0, 1], [0, 0, 0], [0, 1, 0]);
    }
    return new CubeCameraUniformImpl(name, position, viewMat, perspectiveMat);
};
